import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Calendar, ArrowLeft, Tag as TagIcon, User, Eye } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import MarketingLayout from '@/components/marketing/MarketingLayout';
import Seo, { SITE } from '@/components/shared/Seo';
import blogAPI from '@/services/blogAPI';

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }); } catch { return ''; }
}

// Detect whether the content is HTML or Markdown.
function isHtml(s = '') { return /<\/?(p|h[1-6]|img|iframe|video|div|ul|ol|li|a|strong|em)\b/i.test(s); }

// Sanitize + wrap iframes/videos responsively. Simple pass — content is authored by admins only.
function renderContent(content) {
  if (!content) return null;
  if (isHtml(content)) {
    // Wrap videos and iframes so they behave responsively.
    const enhanced = content
      .replace(/<img\b(?![^>]*loading=)/gi, '<img loading="lazy" ')
      .replace(/<iframe/gi, '<iframe loading="lazy" ')
      .replace(/<video/gi, '<video controls preload="metadata" ');
    return <div className="numuv-post-content" dangerouslySetInnerHTML={{ __html: enhanced }} />;
  }
  return (
    <div className="numuv-post-content">
      <ReactMarkdown remarkPlugins={[remarkGfm]}>
        {content}
      </ReactMarkdown>
    </div>
  );
}

export default function BlogPost() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setError(false);
    setData(null);
    blogAPI.getPost(slug).then(setData).catch(() => setError(true));
  }, [slug]);

  if (error) {
    return (
      <MarketingLayout>{() => (
        <section className="mx-auto max-w-[720px] px-4 md:px-6 pt-16 pb-24 text-center">
          <Seo title="Post not found" path={`/blog/${slug}`} noIndex />
          <h1 className="dis text-[28px] font-bold t1">Post not found</h1>
          <p className="mt-3 t3">The post you&apos;re looking for was moved or deleted.</p>
          <Link to="/blog" className="mt-6 inline-flex items-center gap-2 rounded-full aurora-btn px-5 py-2.5 text-[14px] font-semibold text-white"><ArrowLeft className="w-3.5 h-3.5" /> Back to blog</Link>
        </section>
      )}</MarketingLayout>
    );
  }

  if (!data) {
    return (
      <MarketingLayout>{() => (
        <section className="mx-auto max-w-[720px] px-4 md:px-6 pt-16 pb-24 text-center t3">Loading…</section>
      )}</MarketingLayout>
    );
  }

  const p = data.post;
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: p.title,
    description: p.seo_description || p.subtitle,
    datePublished: p.published_at,
    dateModified: p.updated_at,
    image: p.cover_image ? [p.cover_image] : undefined,
    author: { '@type': 'Organization', name: p.author_name || 'Numuv' },
    publisher: { '@type': 'Organization', name: 'Numuv', logo: { '@type': 'ImageObject', url: `${SITE}/logo.png` } },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE}/blog/${p.slug}` },
  };

  return (
    <MarketingLayout>{() => (
      <>
        <Seo
          title={p.seo_title || p.title}
          description={p.seo_description || p.subtitle || `Read "${p.title}" on the Numuv blog.`}
          path={`/blog/${p.slug}`}
          image={p.cover_image || undefined}
          type="article"
          keywords={(p.tags || []).join(', ')}
          jsonLd={jsonLd}
        />
        <article className="mx-auto max-w-[820px] px-4 md:px-6 pt-14 md:pt-20 pb-14">
          <Link to="/blog" className="inline-flex items-center gap-1.5 t3 text-[13px] hover:text-[#6D5EF7]"><ArrowLeft className="w-3.5 h-3.5" /> Back to blog</Link>
          <header className="mt-5">
            <div className="flex flex-wrap items-center gap-3 text-[12px] t3">
              <span className="inline-flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {fmtDate(p.published_at || p.created_at)}</span>
              <span className="inline-flex items-center gap-1.5"><User className="w-3 h-3" /> {p.author_name || 'Numuv Team'}</span>
              <span className="inline-flex items-center gap-1.5"><Eye className="w-3 h-3" /> {p.view_count || 0} views</span>
            </div>
            <h1 className="dis mt-4 text-[32px] md:text-[46px] font-bold leading-[1.1] tracking-[-0.03em] t1" style={{ fontFamily: "'Sora', sans-serif" }}>{p.title}</h1>
            {p.subtitle && <p className="mt-4 text-[16px] md:text-[19px] leading-[26px] md:leading-[30px] t2">{p.subtitle}</p>}
            {(p.tags || []).length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {(p.tags || []).map((t) => (
                  <Link key={t} to={`/blog?tag=${encodeURIComponent(t)}`} className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 bg-[#6D5EF7]/10 text-[#6D5EF7] text-[11.5px] font-medium hover:bg-[#6D5EF7]/20 transition"><TagIcon className="w-3 h-3" />{t}</Link>
                ))}
              </div>
            )}
          </header>
          {p.cover_image && (
            <img src={p.cover_image} alt={p.title} className="mt-8 w-full rounded-[18px] md:rounded-[22px] softlg" loading="eager" />
          )}
          <div className="mt-8 md:mt-10">{renderContent(p.content)}</div>

          <div className="mt-10 md:mt-14 border-t brd pt-6 text-center">
            <p className="dis text-[16px] md:text-[18px] font-semibold t1" style={{ fontFamily: "'Sora', sans-serif" }}>Enjoyed this article?</p>
            <p className="mt-1.5 t3 text-[13.5px]">Get more like it — email <a className="underline underline-offset-4" href="mailto:hello@numuv.app">hello@numuv.app</a>.</p>
          </div>
        </article>

        {data.related?.length > 0 && (
          <section className="mx-auto max-w-[1240px] px-4 md:px-6 pb-16">
            <h2 className="dis text-[20px] md:text-[24px] font-semibold t1" style={{ fontFamily: "'Sora', sans-serif" }}>Related posts</h2>
            <div className="mt-5 grid md:grid-cols-3 gap-5">
              {data.related.map((r) => (
                <Link key={r.id} to={`/blog/${r.slug}`} className="card card-hover soft rounded-[18px] p-5">
                  <p className="dis text-[16px] font-semibold t1" style={{ fontFamily: "'Sora', sans-serif" }}>{r.title}</p>
                  {r.subtitle && <p className="mt-2 t3 text-[13px] line-clamp-2">{r.subtitle}</p>}
                </Link>
              ))}
            </div>
          </section>
        )}
      </>
    )}</MarketingLayout>
  );
}
